import { useState } from "react";
import { Link2, MessageCircle, Mail, Check } from "lucide-react";

/**
 * Generic share block — Copy / WhatsApp / Email.
 * Accepts a `refCode` (user id or member id) to build a referral URL.
 * Used on /report/:token, /membership/success, /park-home-urgent-repairs etc.
 */
export default function ShareBlock({
  refCode = "",
  title = "Know someone dealing with a similar issue?",
  body = "If someone you know is about to approve work or dealing with a problem, feel free to pass this on — it could save them from getting it wrong.",
  className = "",
}) {
  const [copied, setCopied] = useState(false);

  const baseUrl =
    typeof window !== "undefined"
      ? `${window.location.origin}${refCode ? `/?ref=${encodeURIComponent(refCode)}` : "/"}`
      : "https://parkhomesupport.co.uk/";

  const shareText = `Park Home Support — independent guidance before you approve repairs, claims or quotes. ${baseUrl}`;
  const whatsapp = `https://wa.me/?text=${encodeURIComponent(shareText)}`;
  const mailto = `mailto:?subject=${encodeURIComponent(
    "Park Home Support — worth a look before approving any work"
  )}&body=${encodeURIComponent(
    `Came across this and thought of you.\n\nPark Home Support reviews quotes, scope and insurance relevance before park home owners commit to any work or claims. Could save you from getting it wrong.\n\n${baseUrl}`
  )}`;

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(baseUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    } catch {
      /* ignore */
    }
  };

  return (
    <div
      className={`rounded-3xl bg-[#F7F4ED] border border-[#D7E7DF] p-7 md:p-9 ${className}`}
      data-testid="share-block"
    >
      <h3 className="font-display text-xl sm:text-2xl text-[#071A33] leading-tight">
        {title}
      </h3>
      <p className="mt-3 text-[#475569] text-[15px] leading-relaxed max-w-2xl">{body}</p>
      <div className="mt-6 flex flex-wrap gap-3">
        <button
          onClick={onCopy}
          className="inline-flex items-center gap-2 rounded-full bg-white border border-[#D7E7DF] px-5 py-2.5 text-sm font-medium text-[#071A33] hover:bg-[#F7F4ED] transition"
          data-testid="share-copy-btn"
        >
          {copied ? <Check size={16} className="text-[#064E2E]" /> : <Link2 size={16} />}
          {copied ? "Copied!" : "Copy link"}
        </button>
        <a
          href={whatsapp}
          target="_blank"
          rel="noreferrer noopener"
          className="inline-flex items-center gap-2 rounded-full bg-[#25D366] hover:bg-[#1FB857] text-white px-5 py-2.5 text-sm font-medium transition"
          data-testid="share-whatsapp-btn"
        >
          <MessageCircle size={16} /> WhatsApp
        </a>
        <a
          href={mailto}
          className="inline-flex items-center gap-2 rounded-full bg-[#064E2E] hover:bg-[#0F766E] text-white px-5 py-2.5 text-sm font-medium transition"
          data-testid="share-email-btn"
        >
          <Mail size={16} /> Email
        </a>
      </div>
      {refCode && (
        <p className="mt-4 text-xs text-[#94A3B8]">
          Your unique link is tracked — we&apos;ll know who referred new members.
        </p>
      )}
    </div>
  );
}
